// Edit-aware re-parse helpers for the documentCache. Exposes:
//   - `TextEdit`     — an offset-range replacement (the wire shape callers use)
//   - `applyEdits`   — pure offset-stable replay of a TextEdit list onto a string
//   - `parseIncremental` — the SqlGrammar2 incremental-parse seam (v1 falls back
//                          to a full re-parse via the injected `build` callback;
//                          when SqlGrammar2 ships its tree-sitter incremental
//                          API, this module is the single switch point and call
//                          sites stay unchanged)
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § "Service Layer — AST Cache + Incremental Parse".
//
// (plan16 T11): `editsIntersectRange` — a predicate this module used to
// export for the now-deleted `ProceduralAttachment` incremental-retention
// logic in documentCache.ts (plan16 T10) — was deleted here as dead code: its
// sole production caller was that retention filter, and once it was removed
// the function had zero callers left outside its own barrel re-export and
// its own unit tests. See .superpowers/sdd/plan16-docs-delta.md § T11.

import type { ParseOptions, ParsedDocument, SqlRange } from '../../sql-grammar/model';

import type { BuildParsedDocumentFn } from './documentCache';

export type TextEdit = {
  readonly range: SqlRange;
  readonly text: string;
};

export function applyEdits(prevSource: string, edits: readonly TextEdit[]): string {
  if (edits.length === 0) return prevSource;
  const sorted = [...edits].sort((a, b) => b.range.startOffset - a.range.startOffset);
  let out = prevSource;
  for (const e of sorted) {
    out = out.slice(0, e.range.startOffset) + e.text + out.slice(e.range.endOffset);
  }
  return out;
}

export function parseIncremental(
  prev: ParsedDocument,
  edits: readonly TextEdit[],
  newText: string,
  options: ParseOptions,
  build: BuildParsedDocumentFn,
): Promise<ParsedDocument> | ParsedDocument {
  // SqlGrammar2's tree-sitter incremental parse is not yet exposed (see
  // Ralph/SqlGrammar/PRD.md § perf budgets — incremental ≤ 5 ms). The
  // wasm-free `buildParsedDocumentFromSource` is a single-pass scanner that
  // already runs sub-millisecond on 1000-line buffers, so the perf budget is
  // met by full re-parse today; the future incremental swap is transparent.
  void prev;
  void edits;
  return build(newText, options);
}
